// ============================================================================
// HORA vivant d'une SKILLCARD (migration 0315) — PUR, testé.
// MIROIR des fonctions SQL `card_hora_decay_rate` / `card_hora_current` /
// `card_hora_repetition_factor` et du barème de `credit_card_xp_for_attempt`.
// Garder synchro (le test relit les constantes de la migration).
// ============================================================================
// Seul l'HORA GAGNÉ est stocké (`student_cards.xp` + `xp_updated_at`). La
// perte est calculée à l'affichage : décroissance exponentielle continue dont
// le taux journalier λ dépend de la maîtrise m (0..100) :
//   - m < 90  : λ(m) = exp(a + b·m + c·m²), courbe lisse qui passe par
//               90 % → −10 % en 1 mois, 50 % → −10 %/jour, 10 % → −50 %/jour ;
//   - 90..100 : λ décroît linéairement de λ(90) à 0 ;
//   - 100     : stable.
// DÉLAI DE GRÂCE (migration 0345) : l'érosion ne commence que 24 h après le
// dernier crédit — un élève qui vient de faire l'exercice ne voit jamais son
// HORA baisser le jour même (`floor` faisait perdre 1 HORA en quelques minutes).
// Au crédit d'un exercice : xp = min(xp_max, HORA érodé + gains), puis
// `xp_updated_at = now()`.
//
// Gain d'une bonne réponse :
//   max(1, round(question_xp × difficulté × F_maîtrise × F_répétition))
//   F_maîtrise   = 0,5 + maîtrise d'avant / 100            (0,5 → 1,5)
//   F_répétition = 1 / (1 + Σ exp(−Δt / 3 jours))          (même exercice déjà fait)
// ============================================================================

/** Coefficients de ln λ(m) = a + b·m + c·m² (passent exactement par les 3 ancrages). */
export const HORA_DECAY_A = -0.132634158732;
export const HORA_DECAY_B = -0.018646179388;
export const HORA_DECAY_C = -0.00047416968;
/** Au-delà de ce seuil, l'érosion s'annule progressivement jusqu'à 100 %. */
export const HORA_DECAY_FADE_FROM = 90;
/** L'érosion commence ce nombre de jours après le dernier crédit. */
export const HORA_DECAY_GRACE_DAYS = 1;
/** Constante de temps de l'effet « exercice refait » (jours). */
export const HORA_REPETITION_TAU_DAYS = 3;

const DAY_MS = 86_400_000;

const clampMastery = (m: number) => Math.min(100, Math.max(0, Number.isFinite(m) ? m : 0));

function curve(m: number): number {
  return Math.exp(HORA_DECAY_A + HORA_DECAY_B * m + HORA_DECAY_C * m * m);
}

/** Taux d'érosion journalier λ (par jour, continu) selon la maîtrise. */
export function horaDecayRate(mastery: number): number {
  const m = clampMastery(mastery);
  if (m >= 100) return 0;
  if (m >= HORA_DECAY_FADE_FROM) {
    return (curve(HORA_DECAY_FADE_FROM) * (100 - m)) / (100 - HORA_DECAY_FADE_FROM);
  }
  return curve(m);
}

/** Part perdue en 24 h à maîtrise constante (0..1). */
export function horaDailyLossRate(mastery: number): number {
  return 1 - Math.exp(-horaDecayRate(mastery));
}

function toMs(d: string | Date | null | undefined): number | null {
  if (d == null) return null;
  const t = d instanceof Date ? d.getTime() : Date.parse(d);
  return Number.isFinite(t) ? t : null;
}

/** Jours écoulés depuis la dernière mise à jour (≥ 0). */
export function horaElapsedDays(
  updatedAt: string | Date | null | undefined,
  now: Date = new Date(),
): number {
  const t = toMs(updatedAt);
  if (t == null) return 0;
  return Math.max(0, (now.getTime() - t) / DAY_MS);
}

/** HORA affiché = HORA stocké érodé depuis `updatedAt`, après le délai de grâce (arrondi à l'inférieur). */
export function currentHora(
  xpStored: number,
  mastery: number,
  updatedAt: string | Date | null | undefined,
  now: Date = new Date(),
): number {
  const xp = Math.max(0, Math.floor(xpStored || 0));
  const days = Math.max(0, horaElapsedDays(updatedAt, now) - HORA_DECAY_GRACE_DAYS);
  if (xp === 0 || days === 0) return xp;
  return Math.floor(xp * Math.exp(-horaDecayRate(mastery) * days));
}

export type HoraTrend = 'stable' | 'baisse' | 'forte_baisse';

export interface HoraStatus {
  /** HORA stocké (dernier crédit). */
  stored: number;
  /** HORA affiché, après érosion. */
  current: number;
  /** HORA perdu depuis le dernier crédit. */
  lost: number;
  /** Part perdue (0..1). */
  lostPct: number;
  /** Part perdue par jour à ce rythme (0..1). */
  dailyLoss: number;
  trend: HoraTrend;
}

/** Seuils de l'indicateur à côté de l'HORA. */
export const HORA_TREND_STRONG_LOST = 0.25;
// ≈ 10 %/jour (maîtrise ≤ 50 %), marge pour l'arrondi flottant de l'ancrage.
export const HORA_TREND_STRONG_DAILY = 0.095;

/**
 * Indicateur : seulement une perte RÉELLE (au moins 1 HORA) — jamais une
 * prévision. `forte_baisse` si un quart est perdu ou si le rythme dépasse
 * ≈ 10 %/jour.
 */
export function horaTrend(lost: number, lostPct: number, dailyLoss: number): HoraTrend {
  if (lost < 1) return 'stable';
  if (lostPct >= HORA_TREND_STRONG_LOST || dailyLoss >= HORA_TREND_STRONG_DAILY) {
    return 'forte_baisse';
  }
  return 'baisse';
}

export function horaStatus(
  xpStored: number,
  mastery: number,
  updatedAt: string | Date | null | undefined,
  now: Date = new Date(),
): HoraStatus {
  const stored = Math.max(0, Math.floor(xpStored || 0));
  const current = currentHora(stored, mastery, updatedAt, now);
  const lost = stored - current;
  const lostPct = stored > 0 ? lost / stored : 0;
  // Sans HORA, rien ne peut baisser : l'indicateur reste « stable ».
  const dailyLoss = stored > 0 ? horaDailyLossRate(mastery) : 0;
  return { stored, current, lost, lostPct, dailyLoss, trend: horaTrend(lost, lostPct, dailyLoss) };
}

/** Bonus de maîtrise : 0,5 (maîtrise 0) → 1 (50 %) → 1,5 (100 %). */
export function masteryGainFactor(masteryBefore: number): number {
  return 0.5 + clampMastery(masteryBefore) / 100;
}

/**
 * Frein « exercice refait » : 1 / (1 + Σ exp(−Δt / 3 j)) sur les tentatives
 * TERMINÉES antérieures du même exercice. 1re fois ×1 ; 1 h après ×≈0,5 ;
 * 3 jours après ×0,73 ; 1 semaine après ×0,91 ; 2 semaines après ×0,99.
 */
export function repetitionFactor(
  previousAttempts: ReadonlyArray<string | Date>,
  now: Date = new Date(),
): number {
  let sum = 0;
  for (const d of previousAttempts) {
    const t = toMs(d);
    if (t == null) continue;
    const days = Math.max(0, (now.getTime() - t) / DAY_MS);
    sum += Math.exp(-days / HORA_REPETITION_TAU_DAYS);
  }
  return 1 / (1 + sum);
}

export interface HoraGainInput {
  /** HORA de base d'une bonne réponse (`question_xp` effectif). */
  questionXp: number;
  /** Multiplicateur de difficulté de la question (×1 / ×1,5 / ×2). */
  difficultyMult: number;
  /** F_maîtrise × F_répétition (cf. `masteryGainFactor`, `repetitionFactor`). */
  gainFactor: number;
}

/** HORA d'une bonne réponse (arrondi comme `round()` SQL, au moins 1). */
export function horaGain({ questionXp, difficultyMult, gainFactor }: HoraGainInput): number {
  return Math.max(1, Math.round(questionXp * difficultyMult * gainFactor));
}

/** Nouvel HORA stocké après un exercice : érodé + gains, plafonné. */
export function creditedHora(
  xpStored: number,
  masteryBefore: number,
  updatedAt: string | Date | null | undefined,
  gains: number,
  xpMax: number,
  now: Date = new Date(),
): number {
  return Math.min(xpMax, currentHora(xpStored, masteryBefore, updatedAt, now) + Math.max(0, gains));
}
